import React, { useState } from 'react';
import PricingPage from './PricingPage';
import PaymentMethodForm from './PaymentMethodForm';
import { billingService } from '../../services/billingService';
import { PLAN_DESCRIPTIONS, PLAN_FEATURES, PLAN_LABELS } from './planMarketing';
import './BillingSetup.css';
import InlineAlert from '../common/InlineAlert';
import { getUserFacingError } from '../../lib/userFacingErrors';

export default function BillingSetup({
  open,
  onComplete,
  onClose,
  context = 'setup',
  message,
  mode = 'modal',
  trialStatus,
}) {
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  if (!open) return null;

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setStep(2);
  };

  const handlePaymentSuccess = async (pm) => {
    setError(null);
    setCreating(true);
    try {
      await billingService.createSubscription(selectedPlan.tier, pm.id);
      onComplete?.();
    } catch (e) {
      setError(getUserFacingError(e, 'billing-setup'));
    } finally {
      setCreating(false);
    }
  };

  const priceDisplay = `$${(selectedPlan?.price_cents / 100).toFixed(2)}/mo`;
  const submitLabel = `Subscribe · ${priceDisplay}`;
  const selectedFeatures = PLAN_FEATURES[selectedPlan?.tier] || selectedPlan?.features || [];
  const selectedPlanLabel = PLAN_LABELS[selectedPlan?.tier] || selectedPlan?.name;

  return (
    <div className={`${mode === 'page' ? 'billing-setup-page' : 'billing-setup-overlay'} billing-setup-${context}`}>
      <div className={mode === 'page' ? 'billing-setup-page-inner' : 'billing-setup-modal'}>

        <div className={`setup-flow-header${step === 2 ? ' checkout' : ''}`}>
          {onClose && (
            <button
              type="button"
              className="billing-setup-close"
              aria-label="Close billing setup"
              onClick={onClose}
            >
              ×
            </button>
          )}
          {step === 2 && (
            <button
              className="back-to-plans"
              onClick={() => { setSelectedPlan(null); setStep(1); }}
            >
              ← Back to plans
            </button>
          )}

          <div className="setup-stepper" aria-label="Setup progress">
            <div className={`stepper-step${step >= 1 ? ' active' : ''}${step > 1 ? ' done' : ''}`}>
              <div className="stepper-circle">
                {step > 1 ? <span className="stepper-check">✓</span> : <span>1</span>}
              </div>
              <span className="stepper-label">Choose Plan</span>
            </div>
            <div className={`stepper-connector${step >= 2 ? ' active' : ''}`} />
            <div className={`stepper-step${step >= 2 ? ' active' : ''}`}>
              <div className="stepper-circle">
                <span>2</span>
              </div>
              <span className="stepper-label">Payment</span>
            </div>
          </div>
        </div>

        {context === 'trial' && step === 1 && (
          <div className="trial-upgrade-note" role="status">
            <strong>{message || 'Get more conversations, more connected accounts, and a clearer view of your money.'}</strong>
            <span>Connect the pieces of your financial life and keep planning with Wealth-Y.</span>
          </div>
        )}

        {step === 1 && (
          <PricingPage
            onSelectPlan={handlePlanSelect}
            selectedTier={selectedPlan?.tier}
            hideTrust={mode === 'page'}
          />
        )}

        {step === 2 && selectedPlan && (
          <div className="setup-payment-layout">

            {/* Left: plan summary */}
            <div className="setup-plan-summary">
              <div className="summary-card">
                <div className="summary-eyebrow">Selected plan</div>
                <div className="summary-plan-name">
                  {selectedPlanLabel}
                </div>
                <div className="summary-price">
                  {priceDisplay}
                </div>
                <div className="summary-description">
                  {PLAN_DESCRIPTIONS[selectedPlan.tier]}
                </div>
                <div className="summary-quota">
                  {selectedPlan.monthly_prompt_quota} prompts/month included
                </div>

                <div className="summary-divider" />

                <ul className="summary-features">
                  {selectedFeatures.map((f, i) => (
                    <li key={i}>
                      <span className="summary-check">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="summary-billing-note">
                  Billed monthly. Cancel anytime. Overage prompts are billed at $0.25 each.
                </div>
              </div>
            </div>

            {/* Right: payment form */}
            <div className="setup-payment-card">
              <div className="payment-step-header">
                <div>
                  <div className="payment-step-kicker">Secure checkout</div>
                  <h3 className="payment-step-heading">Complete your subscription</h3>
                  <p className="payment-step-sub">
                    Add a payment method to activate {selectedPlanLabel}. You can review or change your plan later.
                  </p>
                </div>
              </div>

              {creating ? (
                <div className="setup-creating">
                  <div className="setup-creating-spinner" />
                  Setting up your subscription…
                </div>
              ) : (
                <PaymentMethodForm
                  onSuccess={handlePaymentSuccess}
                  onError={(e) => setError(getUserFacingError(e, 'payment-method'))}
                  submitLabel={submitLabel}
                />
              )}

              {error && <InlineAlert title={error.title} message={error.message} />}

              <div className="payment-assurance-grid" aria-label="Payment security details">
                <div className="payment-assurance-item">
                  <span className="assurance-icon" aria-hidden="true">✓</span>
                  <div>
                    <strong>Checkout powered by Stripe</strong>
                    <span>Payment is handled by Stripe securely.</span>
                  </div>
                </div>
                <div className="payment-assurance-item">
                  <span className="assurance-icon" aria-hidden="true">✓</span>
                  <div>
                    <strong>We do not store your full card number</strong>
                    <span>We only receive a secure payment method reference.</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
